import { useCallback, useState } from "react";
import {
  Alert,
  Button,
  Center,
  Group,
  Loader,
  Radio,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconDeviceFloppy } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getReportIdentityPreview,
  ReportIdentitySource,
  updatePrefix,
} from "../../../services/prefixService";
import { ReportIdentityPreviewCard } from "./ReportIdentityPreviewCard";

interface PrefixReportIdentityPanelProps {
  prefixId: string;
  initialSource: ReportIdentitySource;
}

const IDENTITY_SOURCES: Array<{
  value: ReportIdentitySource;
  label: string;
  description: string;
}> = [
  {
    value: "gcp",
    label: "Gulf Coast Pathologists",
    description:
      "Use the canonical GCP logo, medical director, contact details, and footer.",
  },
  {
    value: "sending_practice",
    label: "Sending Practice",
    description:
      "Resolve each preview from the practice on the applicable prefix mapping.",
  },
  {
    value: "submitting_lab",
    label: "Submitting Lab",
    description:
      "Resolve each preview from mapped locations whose type is Lab.",
  },
  {
    value: "surgery_center",
    label: "Surgery Center",
    description:
      "Resolve each preview from mapped locations whose type is Surgery Center.",
  },
];

export function PrefixReportIdentityPanel({
  prefixId,
  initialSource,
}: PrefixReportIdentityPanelProps) {
  const [source, setSource] = useState<ReportIdentitySource>(initialSource);
  const queryClient = useQueryClient();
  const previewQuery = useQuery({
    queryKey: ["prefix-report-identity-preview", prefixId, source],
    queryFn: () => getReportIdentityPreview(prefixId, source),
  });
  const updateMutation = useMutation({
    mutationFn: () => updatePrefix(prefixId, { reportIdentitySource: source }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prefix-admin", prefixId] });
      notifications.show({
        title: "Report identity saved",
        message:
          "New reports for this prefix will use the selected identity source.",
        color: "green",
      });
    },
    onError: () => {
      notifications.show({
        title: "Could not save report identity",
        message: "Review the configuration and try again.",
        color: "red",
      });
    },
  });

  const handleSourceChange = useCallback((value: string) => {
    setSource(value as ReportIdentitySource);
  }, []);

  const handleSave = useCallback(() => {
    updateMutation.mutate();
  }, [updateMutation]);

  return (
    <Stack gap="lg">
      <Radio.Group
        label="Identity source"
        description="Select the organization whose logo and header details identify reports for this prefix."
        value={source}
        onChange={handleSourceChange}
      >
        <Stack gap="sm">
          {IDENTITY_SOURCES.map((option) => (
            <Radio.Card
              key={option.value}
              value={option.value}
              withBorder
              p="md"
            >
              <Group wrap="nowrap" align="flex-start">
                <Radio.Indicator />
                <div>
                  <Text fw={500}>{option.label}</Text>
                  <Text size="sm" c="dimmed">
                    {option.description}
                  </Text>
                </div>
              </Group>
            </Radio.Card>
          ))}
        </Stack>
      </Radio.Group>

      <Button
        leftSection={<IconDeviceFloppy size={16} />}
        onClick={handleSave}
        loading={updateMutation.isPending}
        style={{ alignSelf: "flex-start" }}
      >
        Save report identity
      </Button>

      <div>
        <Text fw={600}>Resolved report previews</Text>
        <Text size="sm" c="dimmed">
          A separate preview is shown for every applicable mapped target.
        </Text>
      </div>

      {previewQuery.isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : previewQuery.isError ? (
        <Alert color="red" title="Preview unavailable">
          The report identity preview could not be loaded. Try again.
        </Alert>
      ) : (
        <>
          {previewQuery.data?.warnings.map((warning) => (
            <Alert
              key={warning}
              color="orange"
              icon={<IconAlertTriangle size={16} />}
            >
              {warning}
            </Alert>
          ))}
          {previewQuery.data?.previews.map((preview) => (
            <ReportIdentityPreviewCard
              key={preview.mappingId || preview.targetId || preview.targetName}
              preview={preview}
            />
          ))}
        </>
      )}
    </Stack>
  );
}
